import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { App as AntApp, ConfigProvider, GetProp, Steps, StepsProps, Tabs } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { CompetencePageCheck } from "../../../../../app/contexts/CompetenceCheckContext";
import { useGetCalcDraft, useGetCalcRecordRequestResponse, useGetContract } from "../../../../../common/api/queries";
import { QueryKeys } from "../../../../../common/constants";
import { FieldConstraintViolation } from "../../../../../common/types";
import { FinancialSector } from "../../../../agent/enums";
import { ClientFormType } from "../../../../client/enums";
import { Client, LegalClient, NaturalClient } from "../../../../client/types";
import { processClientsDataViolations } from "../../../../client/utils";
import { DASHBOARD_ROUTE_PATHS } from "../../../../dashboard/paths";
import { CALC_ROUTE_PATHS } from "../../../paths";
import { OperationType } from "../../../records/enums";
import { RealtyCalcRecordRequestResponse } from "../../../records/types";
import { CalcLoadingModal } from "../../components/CalcLoadingModal";
import { CalcResult } from "../../types";
import { createCalcGenDataFromContract, isApplicableResult, isRealtyGenData } from "../../utils";
import { POLICY_HOLDER_INDEX_MAP, RealtyCalcWrapper } from "../components/calc/RealtyCalcWrapper";
import { RealtyGenWrapper } from "../components/gen/RealtyGenWrapper";
import { RealtyCalcResultsView } from "../components/result/RealtyCalcResultsView";
import { RealtyCalcResultsProvider, useRealtyCalcResults } from "../contexts";
import { RealtyCalc, RealtyCalcResultData, RealtyFormClients, RealtyGen, RealtyGenForm } from "../types";
import {
  createRealtyCalcObjectFromCalcData,
  createRealtyCalcObjectFromGenData,
  createRealtyFormClientsObjectFromCalcData,
  createRealtyFormClientsObjectFromGenData,
  createRealtyGenFormDataObject,
} from "../utils";

type StepItem = {
  title: React.ReactNode;
  icon: React.ReactNode;
  content?: React.ReactNode;
};

enum REALTY_CALC_STEPS {
  FIRST_STEP_CALC = 0,
  SECOND_STEP_RESULTS = 1,
  THIRD_STEP_GENERATE_CONTRACT = 2,
}

export const RealtyCalcContainer = () => (
  <RealtyCalcResultsProvider>
    <RealtyCalcContainerContent />
  </RealtyCalcResultsProvider>
);

const RealtyCalcContainerContent = () => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get(QueryKeys.RECORD_ID);
  const draftId = searchParams.get(QueryKeys.DRAFT_ID);
  const contractId = searchParams.get(QueryKeys.CONTRACT_ID);
  const [currentStepIndex, setCurrentStepIndex] = useState<REALTY_CALC_STEPS>(REALTY_CALC_STEPS.FIRST_STEP_CALC);
  const [lastAvailableStepIndex, setLastAvailableStepIndex] = useState<number>(REALTY_CALC_STEPS.FIRST_STEP_CALC);
  const [selectedResult, setSelectedResult] = useState<CalcResult<RealtyCalcResultData>>();
  const [calcData, setCalcData] = useState<RealtyCalc>();
  const [genData, setGenData] = useState<RealtyGenForm>();
  const [clients, setClients] = useState<RealtyFormClients>({
    policyHolder: undefined,
    insured: undefined,
    representative: undefined,
    vinculation: undefined,
  });

  const [calcDataFormErrors, setCalcDataFormErrors] = useState<FieldConstraintViolation[]>([]);
  const [calcClientsViolationErrors, setCalcClientsViolationErrors] = useState<
    Map<ClientFormType, FieldConstraintViolation[]>
  >(() => new Map());
  const [isCalcFormChanged, setIsCalcFormChanged] = useState(false);

  const { data: calcRecord, isError: isErrorCalcRecord } =
    useGetCalcRecordRequestResponse<RealtyCalcRecordRequestResponse>(recordId ?? "");
  const { data: calcDraft, isError: isErrorCalcDraft } = useGetCalcDraft(draftId ?? "");
  const { data: contract, isError: isErrorContract } = useGetContract(contractId ?? "");
  const { applicableResults, calculate, clearResults } = useRealtyCalcResults();

  const clearData = () => {
    clearResults();
  };

  useEffect(() => {
    if (isErrorCalcRecord || isErrorCalcDraft || isErrorContract) {
      clearData();
      navigate(CALC_ROUTE_PATHS.realty.to, { replace: true });
    }
  }, [isErrorCalcRecord, isErrorCalcDraft, isErrorContract]);

  const initFormDataFromGenData = (genData: RealtyGen) => {
    const clients = createRealtyFormClientsObjectFromGenData(genData);
    setClients(clients);
    setCalcData(createRealtyCalcObjectFromGenData(genData, clients));
    setGenData(createRealtyGenFormDataObject(genData, clients));
  };

  useEffect(() => {
    if (calcData || !contract) {
      return;
    }

    const calcGenData = createCalcGenDataFromContract(contract);

    if (!calcGenData || !isRealtyGenData(calcGenData)) {
      notification.error({
        title: t("common.error"),
        description: t("calc.helpers.loadDataError"),
        key: "loadCalcDataError",
      });

      clearData();
      navigate(CALC_ROUTE_PATHS.realty.to, { replace: true });
      return;
    }

    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.helpers.calcDataInitFromContract"),
      duration: 10,
      key: "realtyCalcDataInitFromContract",
    });

    initFormDataFromGenData(calcGenData);
  }, [contract]);

  useEffect(() => {
    if (calcData || !calcRecord) {
      return;
    }
    notification.info({
      title: t("calc.helpers.calcDataInitTitle"),
      description: t("calc.records.helpers.calcDataInit"),
      duration: 10,
      key: "realtyCalcDataInit",
    });

    if (calcRecord.operationType === OperationType.CALCULATE) {
      const clients = createRealtyFormClientsObjectFromCalcData(calcRecord.request);
      setClients(clients);
      setCalcData(createRealtyCalcObjectFromCalcData(calcRecord.request, clients));
    } else {
      initFormDataFromGenData(calcRecord.request);
    }
  }, [calcRecord]);

  useEffect(() => {
    if (calcData || !calcDraft?.draftData) {
      return;
    }

    notification.info({
      title: t("calc.draft.helpers.draftLoaded"),
      description: t("calc.draft.helpers.draftLoadedInfo"),
      duration: 10,
      key: "realtyDraftLoaded",
    });

    const clients = createRealtyFormClientsObjectFromGenData(calcDraft.draftData);
    setClients(clients);
    setCalcData(createRealtyCalcObjectFromGenData(calcDraft.draftData, clients));

    setGenData(createRealtyGenFormDataObject(calcDraft.draftData, clients));
  }, [calcDraft?.draftData]);

  const handleCalculationFormSubmit = (calcData: RealtyCalc) => {
    setCalcData(calcData);
    calculate(calcData, {
      onSuccess: (results) => {
        setIsCalcFormChanged(false);
        setCalcDataFormErrors([]);

        if (results.some(isApplicableResult)) {
          handleNextStep();
        } else if (results.length) {
          resetToCalcStep();
        }
      },
    });
  };

  const handleClientChange = (type: ClientFormType, client?: Partial<Client>): RealtyFormClients => {
    let updatedClients: RealtyFormClients;
    switch (type) {
      case ClientFormType.POLICY_HOLDER:
        updatedClients = { ...clients, policyHolder: client };
        break;
      case ClientFormType.INSURED:
        updatedClients = { ...clients, insured: client as NaturalClient };
        break;
      case ClientFormType.REPRESENTATIVE:
        updatedClients = { ...clients, representative: client as NaturalClient };
        break;
      case ClientFormType.VINCULATION:
        updatedClients = { ...clients, vinculation: client as LegalClient };
        break;
      default:
        updatedClients = { ...clients };
        break;
    }

    setClients(updatedClients);
    return updatedClients;
  };

  const handleResetCalculatorClick = (onSuccess: () => void) => {
    modal.confirm({
      title: t("calc.helpers.resetCalculatorSubmit"),
      okText: t("common.yes"),
      cancelText: t("common.back"),
      onOk: () => {
        setSelectedResult(undefined);
        setClients({ policyHolder: undefined, insured: undefined, representative: undefined, vinculation: undefined });
        setCalcData(undefined);
        setGenData(undefined);

        clearData();
        onSuccess();
        navigate(CALC_ROUTE_PATHS.realty.to, { replace: true });
      },
    });
  };

  const handleGenerateContractClick = (selectedResult: CalcResult<RealtyCalcResultData>) => {
    setSelectedResult(selectedResult);
    handleNextStep();
  };

  const handleShowErrors = (errors: FieldConstraintViolation[]) => {
    setCalcDataFormErrors(errors);

    if (errors.some((error) => error.fieldPath.startsWith("clientsData.clients"))) {
      setCalcClientsViolationErrors(
        processClientsDataViolations(POLICY_HOLDER_INDEX_MAP, "clientsData.clients", errors),
      );
    }

    goToPreviousStep();
  };

  const handleNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);

    if (lastAvailableStepIndex < currentStepIndex + 1) {
      setLastAvailableStepIndex(lastAvailableStepIndex + 1);
    }
  };

  const resetToCalcStep = () => {
    setCurrentStepIndex(REALTY_CALC_STEPS.FIRST_STEP_CALC);
    setLastAvailableStepIndex(REALTY_CALC_STEPS.FIRST_STEP_CALC);
  };

  const handleStepChange = (stepIndex: number) => {
    if (stepIndex === REALTY_CALC_STEPS.SECOND_STEP_RESULTS && applicableResults.length === 0) {
      resetToCalcStep();
    } else {
      setCurrentStepIndex(stepIndex);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const goToNextStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);
  };

  const stepItems: StepItem[] = [
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.calculation")}</div>,
      icon: <AntIcon type="laptop" />,
      content: (
        <RealtyCalcWrapper
          calcData={calcData}
          formErrors={calcDataFormErrors}
          clientsViolationErrors={calcClientsViolationErrors}
          onClientsViolationErrorsChange={setCalcClientsViolationErrors}
          setIsFormChanged={() => setIsCalcFormChanged(true)}
          policyHolder={clients.policyHolder}
          onCalculationFormSubmit={handleCalculationFormSubmit}
          onClientChange={handleClientChange}
          onResetCalculatorClick={handleResetCalculatorClick}
          goToCalcResults={isCalcFormChanged || applicableResults.length === 0 ? undefined : goToNextStep}
        />
      ),
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.insuranceSelection")}</div>,
      icon: <AntIcon type="file-search" />,
      content: calcData ? (
        <RealtyCalcResultsView
          calcData={calcData}
          handleGenerateContract={handleGenerateContractClick}
          handleShowErrors={handleShowErrors}
          handleGoToPreviousStep={goToPreviousStep}
          clients={clients}
          draft={calcDraft}
        />
      ) : undefined,
    },
    {
      title: <div style={{ fontSize: 18 }}>{t("calc.realty.sections.contractGeneration")}</div>,
      icon: <AntIcon type="file-done" />,
      content:
        calcData && selectedResult ? (
          <RealtyGenWrapper
            initialData={genData}
            calcData={calcData}
            clients={clients}
            selectedResult={selectedResult}
            draftId={draftId ?? undefined}
            onClientChange={handleClientChange}
            handleGoToPreviousStep={goToPreviousStep}
          />
        ) : undefined,
    },
  ];

  const steps: GetProp<StepsProps, "items"> = stepItems.map((item, index) => ({
    key: index,
    title: item.title,
    disabled: index > lastAvailableStepIndex || isCalcFormChanged,
    status: currentStepIndex >= index ? "finish" : "wait",
    icon: item.icon,
  }));

  return (
    <DisplayWrapper itemLoaded={recordId || draftId || contractId ? !!calcData : true}>
      <CompetencePageCheck
        sector={FinancialSector.INSURANCES_OR_REINSURANCES}
        redirectTo={DASHBOARD_ROUTE_PATHS.homepage.to}
      >
        <ContentWrapper>
          <div style={{ maxWidth: 800, margin: "10px auto 20px" }}>
            <ConfigProvider theme={{ components: { Steps: { customIconFontSize: 28 } } }}>
              <Steps onChange={handleStepChange} current={currentStepIndex} items={steps} />
            </ConfigProvider>
          </div>

          <Tabs
            activeKey={currentStepIndex.toString()}
            renderTabBar={() => <></>}
            animated
            items={[
              {
                key: REALTY_CALC_STEPS.FIRST_STEP_CALC.toString(),
                label: undefined,
                children: stepItems[REALTY_CALC_STEPS.FIRST_STEP_CALC]?.content,
              },
              {
                key: REALTY_CALC_STEPS.SECOND_STEP_RESULTS.toString(),
                label: undefined,
                children: stepItems[REALTY_CALC_STEPS.SECOND_STEP_RESULTS]?.content,
              },
              {
                key: REALTY_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT.toString(),
                label: undefined,
                children: stepItems[REALTY_CALC_STEPS.THIRD_STEP_GENERATE_CONTRACT]?.content,
              },
            ]}
          />

          <CalcLoadingModal />
        </ContentWrapper>
      </CompetencePageCheck>
    </DisplayWrapper>
  );
};
